import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import '../styles/StoreModal.css';
import logger from '../utils/logger';

const VARIANT_INFO = {
  fight_to_be_king: {
    name: "Fight to be King",
    description: "Duke must return to baseline after capturing King to ascend",
    icon: "⚔️"
  },
  players_choice: {
    name: "Player's Choice",
    description: "Each player chooses their Duke's embodiment",
    icon: "🎯"
  },
  pawns_wild: {
    name: "Pawn's Wild",
    description: "Pawns can promote to Dukes",
    icon: "👑"
  },
  two_dukes: {
    name: "Chess Kingdom (Two Dukes)",
    description: "Two Dukes per player with different embodiments",
    icon: "♟️"
  },
  handicap_mode: {
    name: "Standard Chess vs Royal Chess Kingdom",
    description: "One player uses standard chess, the other uses Royal Chess Kingdom",
    icon: "⚖️"
  },
  all_variants: {
    name: "All Variants Bundle",
    description: "Unlock all paid variants at once (Save 20%!)",
    icon: "🎁"
  }
};

const StoreModal = ({ isOpen, onClose, preselectedVariant = null }) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [notification, setNotification] = useState(null);
  const { token } = useAuth();
  const { t } = useTranslation();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      fetchVariants();
    }
  }, [isOpen]);

  const fetchVariants = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/store/variants`);
      const data = await response.json();
      setVariants(data.variants || []);
    } catch (error) {
      logger.error('Failed to fetch variants:', error);
    }
    setLoading(false);
  };

  const addToCart = (variant) => {
    if (cart.find(item => item.id === variant.id)) {
      showNotification('Item already in cart');
      return;
    }
    setCart([...cart, variant]);
    showNotification(t('store.itemAdded') || 'Item added to cart');
  };

  const removeFromCart = (variantId) => {
    setCart(cart.filter(item => item.id !== variantId));
  };

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 2000);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  const handleDirectPurchase = async (variantId) => {
    setProcessingPayment(true);

    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          variant_id: variantId,
          origin_url: originUrl
        })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        alert(t('store.purchaseFailed') || 'Failed to create checkout session');
        setProcessingPayment(false);
      }
    } catch (error) {
      logger.error('Purchase error:', error);
      alert(t('store.purchaseFailed') || 'An error occurred. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handleCartCheckout = async () => {
    if (cart.length === 0) return;
    
    // For now, checkout the first item (backend would need to support multi-item checkout)
    // In a full implementation, you'd send all cart items to a multi-item checkout endpoint
    setProcessingPayment(true);

    try {
      const originUrl = window.location.origin;
      const variantIds = cart.map(item => item.id);
      
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          variant_id: variantIds.length === 1 ? variantIds[0] : 'cart_items',
          variant_ids: variantIds,
          origin_url: originUrl
        })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        setCart([]); // Clear cart
        window.location.href = data.url;
      } else {
        alert(t('store.purchaseFailed') || 'Failed to create checkout session');
        setProcessingPayment(false);
      }
    } catch (error) {
      logger.error('Purchase error:', error);
      alert(t('store.purchaseFailed') || 'An error occurred. Please try again.');
      setProcessingPayment(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="store-modal-overlay" onClick={onClose}>
      <div className="store-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>

        <div className="store-header">
          <h2>🛍️ {t('store.title') || 'Variant Store'}</h2>
          <button 
            className={`cart-button ${cart.length > 0 ? 'has-items' : ''}`}
            onClick={() => setShowCart(!showCart)}
            data-testid="cart-button"
          >
            🛒 {t('store.cart') || 'Cart'} ({cart.length})
          </button>
        </div>
        
        <p className="store-subtitle">{t('store.themedSets') || 'Unlock premium chess variants'}</p>

        {notification && (
          <div className="store-notification">{notification}</div>
        )}

        {processingPayment && (
          <div className="processing-overlay">
            <div className="spinner"></div>
            <p>Redirecting to secure checkout...</p>
          </div>
        )}

        {showCart ? (
          <div className="cart-view">
            <h3>🛒 {t('store.cart') || 'Your Cart'}</h3>
            {cart.length === 0 ? (
              <p className="empty-cart">{t('store.emptyCart') || 'Your cart is empty'}</p>
            ) : (
              <>
                <div className="cart-items">
                  {cart.map((item) => {
                    const info = VARIANT_INFO[item.id];
                    return (
                      <div key={item.id} className="cart-item">
                        <span className="cart-item-icon">{info?.icon || '📦'}</span>
                        <span className="cart-item-name">{info?.name || item.id}</span>
                        <span className="cart-item-price">${item.price.toFixed(2)}</span>
                        <button 
                          className="remove-item-btn"
                          onClick={() => removeFromCart(item.id)}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="cart-total">
                  <span>{t('store.total') || 'Total'}:</span>
                  <span className="total-amount">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="cart-actions">
                  <button 
                    className="continue-shopping-btn"
                    onClick={() => setShowCart(false)}
                  >
                    {t('store.continueShopping') || 'Continue Shopping'}
                  </button>
                  <button 
                    className="checkout-btn"
                    onClick={handleCartCheckout}
                    disabled={processingPayment}
                    data-testid="checkout-btn"
                  >
                    {t('store.proceedToCheckout') || 'Proceed to Checkout'}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="variants-grid">
              {loading ? (
                <p>Loading variants...</p>
              ) : variants.map((variant) => {
                const info = VARIANT_INFO[variant.id];
                if (!info) return null;

                const isBundle = variant.id === 'all_variants';
                const isInCart = cart.find(item => item.id === variant.id);

                return (
                  <div
                    key={variant.id}
                    className={`variant-card ${isBundle ? 'bundle' : ''} ${preselectedVariant === variant.id ? 'highlighted' : ''}`}
                    data-testid={`variant-${variant.id}`}
                  >
                    <div className="variant-icon">{info.icon}</div>
                    <h3>{info.name}</h3>
                    <p className="variant-description">{info.description}</p>
                    <div className="variant-price">
                      ${variant.price.toFixed(2)}
                      {isBundle && <span className="save-badge">Save 20%</span>}
                    </div>
                    <div className="variant-actions">
                      <button
                        className={`add-to-cart-btn ${isInCart ? 'in-cart' : ''}`}
                        onClick={() => addToCart(variant)}
                        disabled={isInCart}
                        data-testid={`add-to-cart-${variant.id}`}
                      >
                        {isInCart ? '✓ In Cart' : (t('store.addToCart') || 'Add to Cart')}
                      </button>
                      <button
                        className="purchase-button"
                        onClick={() => handleDirectPurchase(variant.id)}
                        disabled={processingPayment}
                        data-testid={`buy-now-${variant.id}`}
                      >
                        {processingPayment ? 'Processing...' : (t('store.unlockSet') || 'Buy Now')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="store-footer">
              <p>✅ One-time purchase, unlock forever</p>
              <p>✅ Secure payment via Stripe</p>
              <p>✅ Instant access after payment</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoreModal;
